
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

// Define a more flexible type that matches what we're actually using
type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
};

export function useAdjacentPosts(currentPostId: string | null, currentPostDate: string | null) {
  const [previousPost, setPreviousPost] = useState<BlogPost | null>(null);
  const [nextPost, setNextPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If no valid IDs provided, don't load anything but don't show as loading
    if (!currentPostId || !currentPostDate) {
      setPreviousPost(null);
      setNextPost(null);
      setLoading(false);
      return;
    }

    // Only set loading if we have valid data to load
    setLoading(true);

    // Create abort controller for cleanup
    const abortController = new AbortController();
    
    const fetchAdjacentPosts = async () => {
      if (!currentPostId || !currentPostDate) {
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching adjacent posts for", currentPostId, currentPostDate);
        
        // Only proceed if not aborted
        if (abortController.signal.aborted) return;

        // Fetch previous post (older post) with more data
        const { data: prevPost, error: prevError } = await supabase
          .from('blog_posts')
          .select(`
            id, 
            title, 
            slug, 
            created_at, 
            excerpt, 
            featured_image,
            author_id,
            tags:blog_posts_tags(tag_id(id, name))
          `)
          .eq('status', 'published')
          .lt('created_at', currentPostDate)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (prevError) {
          console.error("Error fetching previous post:", prevError);
        }

        // Fetch next post (newer post) with more data
        const { data: nextPost, error: nextError } = await supabase
          .from('blog_posts')
          .select(`
            id, 
            title, 
            slug, 
            created_at,
            excerpt,
            featured_image,
            author_id,
            tags:blog_posts_tags(tag_id(id, name))
          `)
          .eq('status', 'published')
          .gt('created_at', currentPostDate)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
          
        if (nextError) {
          console.error("Error fetching next post:", nextError);
        }

        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          console.log("Previous post:", prevPost);
          console.log("Next post:", nextPost);
          
          // Transform previous post data
          if (prevPost) {
            // Transform the tags array
            const prevPostWithTags = {
              ...prevPost,
              tags: prevPost.tags?.map(tag => ({
                id: tag.tag_id?.id || '',
                name: tag.tag_id?.name || ''
              })) || []
            };
            setPreviousPost(prevPostWithTags);
          } else {
            setPreviousPost(null);
          }

          // Transform next post data
          if (nextPost) {
            // Transform the tags array
            const nextPostWithTags = {
              ...nextPost,
              tags: nextPost.tags?.map(tag => ({
                id: tag.tag_id?.id || '',
                name: tag.tag_id?.name || ''
              })) || []
            };
            setNextPost(nextPostWithTags);
          } else {
            setNextPost(null);
          }
          
          setLoading(false);
        }
      } catch (err) {
        // Only log error if not aborted
        if (!abortController.signal.aborted) {
          console.error('Error fetching adjacent posts:', err);
          setLoading(false);
        }
      }
    };

    fetchAdjacentPosts();

    // Cleanup function to abort any in-flight requests
    return () => {
      abortController.abort();
    };
  }, [currentPostId, currentPostDate]);

  return { previousPost, nextPost, loading };
}
